import { FC } from 'react';
import { useFlag } from '@reflag/react-sdk';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Row, Column } from 'further-ui/components/Layout';
import {
  CalendarDay,
  numberToCurrencyString,
  numberToPercentageString,
  toPercentage,
} from 'further-ui/utils';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import CapitalActionsSummaryStat from './CapitalActionsSummaryStat';
import CapitalProgress from './CapitalProgress';

const Summary = styled(Column)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  gap: theme.spacing(theme.spacingSizes.lg),
}));

type CapitalActionsSummaryContentProps = {
  vehicleId: string;
  baseCurrency?: string;
};

const CapitalActionsSummaryContent: FC<CapitalActionsSummaryContentProps> = ({
  vehicleId,
  baseCurrency,
}) => {
  const { isEnabled: showBankRecDataInCapitalNotices } = useFlag(
    'show-bank-rec-data-in-capital-notices',
  );

  const [
    {
      totalCommittedCapitalLcy,
      totalPaidInCapitalLcy,
      recallableDistributionsLcy,
      availableToCallLcy,
    },
  ] = api.capitalAction.getVehicleCapitalActionsSummary.useSuspenseQuery({
    vehicleId,
  });

  const paidInPercentage = toPercentage(
    totalPaidInCapitalLcy,
    totalCommittedCapitalLcy,
  );

  return (
    <Column spacing="lg">
      <Column spacing="xs">
        <Typography variant="h5">Vehicle summary</Typography>
        <Typography variant="caption">
          Excluding outside commitment capital
        </Typography>
      </Column>
      <Summary>
        <Row spacing="lg" breakpoint="md" justify="space-between">
          <Row spacing="lg" breakpoint="sm" justify="space-between" flex={1}>
            <CapitalActionsSummaryStat
              label="Total committed capital"
              value={numberToCurrencyString(totalCommittedCapitalLcy, {
                currency: baseCurrency,
                fallback: '--',
              })}
              description={`As at ${new CalendarDay(new Date()).toReadable()}`}
            />
            <CapitalActionsSummaryStat
              label="Total paid-in capital"
              value={numberToCurrencyString(totalPaidInCapitalLcy, {
                currency: baseCurrency,
                fallback: '--',
              })}
              description={numberToPercentageString(paidInPercentage, {
                accuracy: 2,
                noMultiply: true,
                forceDecimals: true,
              })}
            />
          </Row>
          <Row spacing="lg" breakpoint="sm" justify="space-between" flex={1}>
            <CapitalActionsSummaryStat
              label="Recallable distributions"
              value={numberToCurrencyString(recallableDistributionsLcy, {
                currency: baseCurrency,
                fallback: '--',
              })}
              description={numberToPercentageString(
                toPercentage(
                  recallableDistributionsLcy,
                  totalCommittedCapitalLcy,
                ),
                {
                  accuracy: 2,
                  noMultiply: true,
                  forceDecimals: true,
                },
              )}
            />
            <CapitalActionsSummaryStat
              label="Available to call"
              value={numberToCurrencyString(availableToCallLcy, {
                currency: baseCurrency,
                fallback: '--',
              })}
              description={numberToPercentageString(
                toPercentage(availableToCallLcy, totalCommittedCapitalLcy),
                {
                  accuracy: 2,
                  noMultiply: true,
                  forceDecimals: true,
                },
              )}
            />
          </Row>
        </Row>
        {showBankRecDataInCapitalNotices ? (
          <>
            <Divider />
            <Column spacing="sm">
              <CapitalProgress value={paidInPercentage} />
              <Typography variant="h6" color="grey.600">
                {numberToPercentageString(paidInPercentage, {
                  accuracy: 2,
                  noMultiply: true,
                  forceDecimals: true,
                })}{' '}
                paid-in
              </Typography>
            </Column>
          </>
        ) : null}
      </Summary>
    </Column>
  );
};

const CapitalActionsSummary: FC = () => {
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const baseCurrency = vehicle?.baseCurrency;

  if (!selectedVehicleId) {
    return null;
  }

  return (
    <CapitalActionsSummaryContent
      vehicleId={selectedVehicleId}
      baseCurrency={baseCurrency}
    />
  );
};

export default CapitalActionsSummary;
